import * as React from "react"
import { type MonetaryValue } from "@digits-graphql/frontend/graphql-bearer"
import { CopyableAmount, CopyableAmountContainer } from "@digits-shared/DesignSystem/CopyableAmount"
import {
  COMMON_INPUT_STYLES,
  HintText,
  InputLabel,
} from "@digits-shared/DesignSystem/FormElements/Input"
import zIndexes from "@digits-shared/DesignSystem/zIndexes"
import numberHelper from "@digits-shared/helpers/numberHelper"
import useForwardedRef from "@digits-shared/hooks/useForwardedRef"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"
import styled, { css } from "styled-components"

/*
  STYLES
*/

const StyledInput = styled.input<{ $invalid?: boolean }>`
  ${COMMON_INPUT_STYLES};
  text-align: right;
`

const InteractiveAmount = styled.div`
  position: relative;
  width: 100%;
`

const FormattedAmount = styled.div<{ $showPlaceholder: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  text-align: right;

  ${fonts.scale.body};

  border-radius: 8px;
  border: 1px solid ${colors.secondary30};
  padding: 0 8px 0 4px;

  display: flex;
  align-items: center;
  justify-content: flex-end;

  span {
    position: relative;
    text-align: center;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-block;
    overflow: hidden;
    max-width: 100%;
    padding-left: 8px;

    ${({ $showPlaceholder }) =>
      $showPlaceholder
        ? css`
            color: ${colors.secondary50};
          `
        : css`
            &:before {
              content: "$";
              position: absolute;
              left: 0;
            }
          `};
  }
`

const InputContainer = styled.div<{ $small: boolean; $valid: boolean; $disabled: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: relative;
  gap: 2px;

  ${CopyableAmountContainer} {
    pointer-events: auto;
  }

  &:hover ${CopyableAmountContainer} {
    opacity: 1;
    z-index: ${zIndexes.mainContent};
  }

  ${StyledInput}, ${FormattedAmount} {
    height: ${({ $small }) => ($small ? "32px" : "40px")};
  }

  ${InteractiveAmount} {
    opacity: 0;
  }

  &:focus-within {
    ${InteractiveAmount} {
      opacity: 1;
    }

    ${FormattedAmount} {
      opacity: 0;
    }
  }

  ${({ $valid }) =>
    $valid
      ? css`
          &:hover {
            ${FormattedAmount} {
              border-color: ${colors.secondary50};
              background: ${colors.white};
            }
          }
        `
      : css`
          ${FormattedAmount} {
            border: 1px solid ${colors.error};
          }

          &:hover {
            ${FormattedAmount} {
              outline: 0.5px solid ${colors.error};
            }
          }

          ${StyledInput}:focus {
            border-color: ${colors.error};
            outline-color: ${colors.error};
          }
        `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      pointer-events: none;

      ${FormattedAmount} {
        background: var(--accounting-background-inputs-disabled-gradient);
        border: none;
      }
    `}
`

const CopyableOverlay = styled.div`
  position: absolute;
  left: 5px;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

/*
  INTERFACE
*/

interface InputProp extends React.InputHTMLAttributes<HTMLInputElement> {
  className?: string
  invalid?: boolean
  small?: boolean
  label?: string
  hintText?: string
}

export interface AmountInputProps
  extends Omit<InputProp, "type" | "onChange" | "onBlur" | "min" | "max" | "defaultValue"> {
  value: number | undefined
  abs?: boolean
  onChange?: (number: number) => void
  onBlur?: (number: number | undefined) => void
}

/*
  COMPONENT
*/
export const AmountInput = React.forwardRef<HTMLInputElement, AmountInputProps>((props, ref) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const inputRef = useForwardedRef(ref)

  const {
    className,
    label,
    hintText,
    value,
    small = false,
    onChange: inputOnChange,
    onBlur: inputOnBlur,
    onFocus: inputOnFocus,
    placeholder,
    abs = false,
    ...inputProps
  } = props
  const { disabled = false } = props

  // internalInputValue is used to remember the raw value of the input while the user is typing
  const [internalInputValue, setInternalInputValue] = React.useState<string | undefined>(undefined)

  // inputValue is what is actually bound to the text field
  const [inputValue, setInputValue] = React.useState<string | number>(value ?? "")

  React.useEffect(() => {
    setInputValue(internalInputValue ?? value ?? "")
  }, [internalInputValue, value])

  // formattedValue is the value that is displayed when the user is not typing
  const [formattedValue, valid] = React.useMemo(() => {
    const formatted =
      inputValue !== ""
        ? numberHelper
            .numberFormatter({
              currency: "USD",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
            .format(value ?? 0)
        : undefined

    // If the user is typing something that can't be calculated, the input is invalid
    const isValid = !(internalInputValue && !numberHelper.calculate(internalInputValue))

    return [formatted, isValid]
  }, [inputValue, internalInputValue, value])

  const onChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInternalInputValue(e.currentTarget.value)
      setInputValue(e.currentTarget.value)

      // Fire the change handler if the user clears the input or if the
      // calculation returns a valid number
      const number = numberHelper.calculate(e.currentTarget.value, abs)
      if (e.currentTarget.value === "" || number !== undefined) {
        inputOnChange?.(number || 0)
      }
    },
    [inputOnChange, abs]
  )

  const onFocus = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      e.target.select()
      inputOnFocus?.(e)
    },
    [inputOnFocus]
  )

  const onBlur = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (isBlurInside(containerRef, e)) {
        return
      }

      // Clear our internal value as the user is done editing
      setInternalInputValue(undefined)

      const result = numberHelper.calculate(e.currentTarget.value, abs)
      setInputValue(result ?? value ?? "")
      inputOnBlur?.(result)
    },
    [inputOnBlur, abs, value]
  )

  const onContainerBlur = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (isBlurInside(containerRef, e)) {
        return
      }
      inputRef.current.blur()
    },
    [inputRef]
  )

  const monetaryValue = React.useMemo((): MonetaryValue => {
    const mv = numberHelper.buildMonetaryAmount()
    return {
      ...mv,
      amount: (value ?? 0) * mv.currencyMultiplier,
    }
  }, [value])

  return (
    <InputContainer
      ref={containerRef}
      onBlur={onContainerBlur}
      $small={small}
      $valid={valid}
      $disabled={disabled}
      className={className}
    >
      <CopyableOverlay>
        <CopyableAmount amount={monetaryValue} />
      </CopyableOverlay>
      {label && (
        <InputLabel htmlFor={inputProps.id}>
          {label}
          {hintText && <HintText>{hintText}</HintText>}
        </InputLabel>
      )}

      <FormattedAmount $showPlaceholder={!formattedValue}>
        <span>{formattedValue || placeholder}</span>
      </FormattedAmount>

      <InteractiveAmount>
        <StyledInput
          {...inputProps}
          value={inputValue}
          ref={inputRef}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          autoComplete="off"
          data-1p-ignore
        />
      </InteractiveAmount>
    </InputContainer>
  )
})

function isBlurInside(containerRef: React.RefObject<HTMLDivElement | null>, e: React.FocusEvent) {
  let clickTarget = e.relatedTarget
  while (clickTarget && clickTarget !== containerRef.current) {
    clickTarget = clickTarget.parentElement
  }
  return !!clickTarget
}
